/**
 * ZoneMapData.ts - High-resolution 160x160 Tile Map Specifications for All 7 Terra Anima (Solaris Kingdom) Zones
 * 
 * DAoC-Scale Full World Specifications:
 * - Zone 1: Solaria Hills (솔라리아 구릉지) - Solarium Citadel, Oakhaven Village, Solaria Crossroads
 * - Zone 2: Grandell Plains (그란델 평원) - Cromlech Megaliths, Windmill Town, Farmlands
 * - Zone 3: Sylvana Forest (실바나 대삼림) - Sage's Barrow, Lakewood Village, Lake
 * - Zone 4: Boreas Peaks (보레아스 설원) - Snow Giant's Throne, Ironfrost Hold, Glacier
 * - Zone 5: Nebula Marsh (네뷸라 늪지) - Lumina Portal Harbor, Reedwater Stilt, Swamps
 * - Zone 6: Ignis Wastes (이그니스 황무지) - Ignar the Dragon's Caldera, Mirage Oasis Camp, Lava Desert
 * - Zone 7: Isle of Lumina (루미나 성역섬) - Sir Baran's Citadel, Lumina Crystal City, Sacred Lake
 */

export type ZoneTileType =
  | 'grass'           // 기본 초원
  | 'hills_grass'     // 높은 구릉 언덕 잔디
  | 'cobblestone'     // 조약돌 포장 왕도
  | 'dirt_road'       // 흙길 / 오솔길
  | 'water'           // 강 / 해자 / 바다 / 호수
  | 'stone_wall'      // 성벽 / 석조 벽체
  | 'wooden_wall'     // 목조 벽체 / 통나무 방책 벽
  | 'stone_pavement'  // 성내 광장 / 석조 보도
  | 'castle_gate'     // 도성 성문 / 관문
  | 'house_roof'      // 민가 오두막 지붕
  | 'farmland'        // 농경지 / 황금 밀밭
  | 'megalith'        // 고대 크롬레크 거석
  | 'altar'           // 고대 룬 제단
  | 'bridge'          // 다리
  | 'fence'           // 울타리
  | 'snow'            // 보레아스 설원 만년설
  | 'ice'             // 보레아스 설원 빙판
  | 'sand'            // 이그니스 황무지 붉은 모래
  | 'lava'            // 이그니스 황무지 용암 분화구
  | 'swamp'           // 네뷸라 늪지 진흙물
  | 'swamp_grass'     // 습지 갈대밭
  | 'crystal_floor';  // 루미나 성역섬 신성 크리스탈 바닥

export interface ZoneProp {
  id: string;
  type: string;
  name: string;
  x: number;
  y: number;
}

export interface ZoneLandmark {
  id: string;
  name: string;
  englishName: string;
  x: number;
  y: number;
  description: string;
  icon: string;
}

export interface ZoneExit {
  direction: 'north' | 'south' | 'east' | 'west';
  targetZoneId: number;
  targetZoneName: string;
  startX: number;
  endX: number;
  startY: number;
  endY: number;
  targetSpawnX: number;
  targetSpawnY: number;
}

export interface StableMasterConfig {
  id: string;
  name: string;
  x: number;
  y: number;
  routes: Array<{
    targetZoneId: number;
    destinationName: string;
    fare: number;
  }>;
}

export interface ZoneData {
  id: number;
  name: string;
  englishName: string;
  biome: string;
  width: number;
  height: number;
  tiles: ZoneTileType[][];
  props: ZoneProp[];
  landmarks: ZoneLandmark[];
  exits: ZoneExit[];
  stableMaster: StableMasterConfig;
}

export const ZONE_SIZE = 160;

// ============================================================================
// ZONE 1: 솔라리아 구릉지 (Solaria Hills) - 160x160 (유저 핸드드로잉 100% 반영)
// ============================================================================
export function generateSolariaHills(): ZoneData {
  const width = ZONE_SIZE, height = ZONE_SIZE;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('grass');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // 1. 대가도 도로망 (North Highway: x: 83..87, y: 0..72, East Highway: y: 68..72, x: 83..159)
  for (let y = 0; y <= 72; y++) {
    for (let w = 83; w <= 87; w++) tiles[y][w] = 'cobblestone';
  }
  for (let x = 83; x < width; x++) {
    for (let w = 68; w <= 72; w++) tiles[w][x] = 'cobblestone';
  }

  // 성채 대각선 가도 (Portal 1 (15, 23) -> Crossroads (85, 70))
  for (let t = 0; t <= 100; t++) {
    const rx = Math.round(15 + (85 - 15) * (t / 100));
    const ry = Math.round(23 + (70 - 23) * (t / 100));
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
      const ny = ry + dy, nx = rx + dx;
      if (ny >= 0 && ny < height && nx >= 0 && nx < width) tiles[ny][nx] = 'cobblestone';
    }
  }

  // 1-B. 은빛여울 강물 (water) & 솔라리스 은빛여울 돌다리 (bridge)
  // 유저 핸드드로잉 160 스케일 반영: (52, 0) -> (37, 37) -> (0, 90)
  const riverSpline160 = [
    [52, 0], [51, 8], [49, 15], [46, 23], [41, 30],
    [37, 37], [30, 45], [23, 52], [19, 60], [16, 68],
    [13, 75], [7, 83], [0, 90]
  ];
  const riverMask160 = Array.from({ length: height }, () => new Uint8Array(width));

  for (let i = 0; i < riverSpline160.length - 1; i++) {
    const p1 = riverSpline160[i], p2 = riverSpline160[i + 1];
    const dist = Math.hypot(p2[0] - p1[0], p2[1] - p1[1]);
    const steps = Math.ceil(dist * 2);
    for (let s = 0; s <= steps; s++) {
      const rx = Math.round(p1[0] + (p2[0] - p1[0]) * (s / steps));
      const ry = Math.round(p1[1] + (p2[1] - p1[1]) * (s / steps));
      for (let dy = -3; dy <= 3; dy++) {
        for (let dx = -3; dx <= 3; dx++) {
          if (dx * dx + dy * dy <= 9) {
            const ny = ry + dy, nx = rx + dx;
            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              tiles[ny][nx] = 'water';
              riverMask160[ny][nx] = 1;
            }
          }
        }
      }
    }
  }

  // 솔라리스 은빛여울 돌다리 (길과 강물이 만나는 교차점 tiles[ny][nx] = 'bridge')
  for (let t = 24; t <= 38; t++) {
    const rx = Math.round(15 + (85 - 15) * (t / 100));
    const ry = Math.round(23 + (70 - 23) * (t / 100));
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        const ny = ry + dy, nx = rx + dx;
        if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
          if (riverMask160[ny][nx] || (nx >= 32 && nx <= 42 && ny >= 33 && ny <= 41)) {
            tiles[ny][nx] = 'bridge';
          }
        }
      }
    }
  }

  // 2. 솔라리스 성 성벽 및 내부 요새 기단 (stone_wall - 절대 이동 불가 & 빈 공간 완전 밀폐)
  // A. 성벽 안쪽(북서부 전역: 5*x + 4*y <= 162 및 관문 뒤편 y <= 22)을 완벽한 성채 석조 기단으로 밀폐
  for (let y = 0; y <= 45; y++) {
    for (let x = 0; x <= 35; x++) {
      if (5 * x + 4 * y <= 162 || (x <= 16 && y <= 22)) {
        tiles[y][x] = 'stone_wall';
      }
    }
  }
  // B. 서부 수직 성벽 배후 밀폐: (0, 40) -> (4, 70)
  for (let y = 40; y <= 70; y++) {
    for (let x = 0; x <= 4; x++) {
      tiles[y][x] = 'stone_wall';
    }
  }
  for (let x = 0; x <= 6; x++) tiles[70][x] = 'stone_wall';

  // 2-B. 2층 높이 입체 3D 석조 성벽 (castle_wall) 및 성채 망루 (castle_tower) 실체화 배치
  // A. 대각선 성벽 라인: (32, 0) -> (0, 40)
  const placedWalls = new Set<string>();
  for (let t = 0; t <= 40; t++) {
    const wx = Math.round(32 - 32 * (t / 40));
    const wy = Math.round(0 + 40 * (t / 40));
    // 관문(15, 23) 앞뒤 통로는 성문이 자리하므로 벽체 제외
    if (Math.hypot(wx - 15, wy - 23) < 2.2) continue;
    const key = `${wx}_${wy}`;
    if (!placedWalls.has(key)) {
      placedWalls.add(key);
      // 북쪽 끝과 모서리는 대형 망루 타워 배치
      if ((wx === 32 && wy === 0) || (wx === 0 && wy === 40)) {
        props.push({ id: `z1_tower_${wx}_${wy}`, type: 'castle_tower', name: '솔라리스 성채 외곽 망루', x: wx, y: wy });
      } else {
        props.push({ id: `z1_wall_${wx}_${wy}`, type: 'castle_wall', name: '솔라리스 2층 석조 성벽', x: wx, y: wy });
      }
    }
  }

  // B. 서부 수직 성벽 라인: (0, 40) -> (3, 70)
  for (let y = 41; y <= 70; y++) {
    // 하우징존 관문(4, 54) 자리는 제외
    if (Math.abs(y - 54) < 3) continue;
    const x = Math.min(3, Math.round((y - 40) * (3 / 30)));
    const key = `${x}_${y}`;
    if (!placedWalls.has(key)) {
      placedWalls.add(key);
      if (y === 70) {
        props.push({ id: `z1_tower_${x}_${y}`, type: 'castle_tower', name: '솔라리스 남서부 망루', x: x, y: y });
      } else {
        props.push({ id: `z1_wall_${x}_${y}`, type: 'castle_wall', name: '솔라리스 2층 석조 성벽', x: x, y: y });
      }
    }
  }

  // C. 성벽 안쪽(배경)에 웅장한 도성 전경 배치 (빈 공간 완전 차폐 & 웅장한 왕국 수도 연출)
  props.push({ id: 'z1_bg_citadel_1', type: 'palace', name: '솔라리스 도성 중앙 대궁전 첨탑', x: 10, y: 10 });
  props.push({ id: 'z1_bg_citadel_2', type: 'castle_tower', name: '솔라리스 왕립 대성채 감시대', x: 20, y: 5 });
  props.push({ id: 'z1_bg_citadel_3', type: 'castle_tower', name: '솔라리스 왕실 근위대 병영 망루', x: 5, y: 22 });
  props.push({ id: 'z1_bg_citadel_4', type: 'citadel_spire', name: '솔라리스 왕궁 태양의 첨탑', x: 14, y: 14 });
  props.push({ id: 'z1_bg_citadel_5', type: 'castle_wall', name: '솔라리스 도성 내벽 흉벽', x: 8, y: 18 });
  props.push({ id: 'z1_bg_citadel_6', type: 'castle_wall', name: '솔라리스 도성 내벽 흉벽', x: 16, y: 12 });

  // 3. 포탈 1: 솔라리스 성 2층 관문 성루 (15, 23)
  tiles[23][15] = 'castle_gate';
  // 관문 바로 뒤편 타일은 철저히 성벽으로 차단하여 포탈 뒤로 진입 불가
  tiles[22][15] = 'stone_wall';
  tiles[22][14] = 'stone_wall';
  tiles[22][16] = 'stone_wall';
  props.push({ id: 'z1_gate_castle', type: 'castle_gate', name: '솔라리스 성 관문 (포탈 1)', x: 15, y: 23 });

  // 4. 포탈 2: 하우징존 관문 (4, 54)
  tiles[54][4] = 'castle_gate';
  props.push({ id: 'z1_gate_housing', type: 'castle_gate', name: '하우징존 관문 (포탈 2)', x: 4, y: 54 });

  // 5. 포인트 3: 오크헤이븐 여관 & 마구간지기 잭슨
  // 오크헤이븐 여관 (x: 85..101, y: 53..66)
  for (let y = 53; y <= 66; y++) for (let x = 85; x <= 101; x++) {
    if (x === 85 || x === 101 || y === 53 || y === 66) {
      // 오크헤이븐 입구 통로(남쪽 중앙 92..94)는 개방하고 나머지는 목조 벽체로 구성
      if (y === 66 && x >= 92 && x <= 94) {
        tiles[y][x] = 'cobblestone';
      } else {
        tiles[y][x] = 'wooden_wall';
      }
    } else {
      tiles[y][x] = 'house_roof';
    }
  }
  props.push({ id: 'z1_inn_oakhaven', type: 'house_cottage', name: '오크헤이븐 여관', x: 93, y: 60 });

  // 마구간지기 잭슨 (x: 86..94, y: 69..75)
  for (let y = 69; y <= 75; y++) for (let x = 86; x <= 94; x++) {
    tiles[y][x] = 'cobblestone';
  }
  props.push({ id: 'z1_stable_jackson', type: 'horse_stable', name: '마구간지기 잭슨', x: 89, y: 72 });

  // 6. 포인트 4: 남서부 2단 언덕 & 고대 공동묘지 던전 입구
  const hillCx = 26, hillCy = 145;
  for (let y = hillCy - 24; y <= Math.min(height - 1, hillCy + 24); y++) {
    for (let x = Math.max(0, hillCx - 24); x <= hillCx + 24; x++) {
      const d = Math.hypot(x - hillCx, y - hillCy);
      if (d <= 22) tiles[y][x] = 'hills_grass'; // 1단 언덕
      if (d <= 14) tiles[y][x] = 'hills_grass'; // 2단 고원
      if (d <= 7) {
        tiles[y][x] = 'stone_pavement'; // 석조 원형 제단 바닥
        if (d >= 6 && d <= 7 && !(x >= hillCx - 1 && x <= hillCx + 1 && y > hillCy)) {
          tiles[y][x] = 'stone_wall';
        }
      }
    }
  }
  props.push({ id: 'z1_gate_cemetery', type: 'dungeon_gate', name: '고대 공동묘지 던전 입구 (포탈 4)', x: hillCx, y: hillCy });

  // 참나무들 배치 (강물 및 도로 외곽 자연 조경)
  [
    { x: 58, y: 20 }, { x: 60, y: 40 }, { x: 75, y: 30 }, { x: 110, y: 45 },
    { x: 120, y: 80 }, { x: 50, y: 100 }, { x: 85, y: 110 }, { x: 130, y: 125 }
  ].forEach((p, idx) => props.push({ id: `z1_oak_${idx}`, type: 'tree_oak', name: '솔라리스 참나무', x: p.x, y: p.y }));

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_solaris_crossroads', name: '솔라리스 언덕 중앙 십자로', englishName: 'Solaris Crossroads', x: 85, y: 70, description: '북쪽 보레아스 설원, 동쪽 그란델 평원, 서쪽 솔라리스 성 분기점.', icon: '🧭' },
    { id: 'landmark_silverstream_bridge', name: '은빛여울 돌다리', englishName: 'Silverstream Bridge', x: 37, y: 37, description: '은빛여울 강을 건너 솔라리스 성으로 향하는 아치형 석조 교량.', icon: '🌉' },
    { id: 'landmark_solaris_castle_gate', name: '솔라리스 성 관문 (포탈 1)', englishName: 'Solaris Castle Gate', x: 15, y: 23, description: '솔라리스 왕국 도성 (128x128 존) 진입 관문.', icon: '🏰' },
    { id: 'landmark_housing_gate', name: '하우징존 관문 (포탈 2)', englishName: 'Housing Zone Gate', x: 4, y: 54, description: '모험가 개인 및 길드 영지 지구 (160x160 존) 진입 관문.', icon: '🏡' },
    { id: 'landmark_oakhaven_inn', name: '오크헤이븐 여관 (포인트 3)', englishName: 'Oakhaven Inn', x: 93, y: 60, description: '초보 모험가 쉼터 & 마구간 거점.', icon: '🍺' },
    { id: 'landmark_cemetery_gate', name: '고대 공동묘지 입구 (포탈 4)', englishName: 'Cemetery Gate', x: 26, y: 145, description: '남서부 2단 언덕 꼭대기의 3층 지하 미로 던전 입구.', icon: '🪦' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'north', targetZoneId: 4, targetZoneName: '보레아스 설원', startX: 83, endX: 87, startY: 0, endY: 0, targetSpawnX: 80, targetSpawnY: 154 },
    { direction: 'east', targetZoneId: 2, targetZoneName: '그란델 평원', startX: 159, endX: 159, startY: 68, endY: 72, targetSpawnX: 6, targetSpawnY: 80 },
    { direction: 'west', targetZoneId: 8, targetZoneName: '솔라리스 성', startX: 13, endX: 17, startY: 21, endY: 25, targetSpawnX: 64, targetSpawnY: 118 },
    { direction: 'west', targetZoneId: 9, targetZoneName: '솔라리스 하우징 지구', startX: 2, endX: 6, startY: 52, endY: 56, targetSpawnX: 80, targetSpawnY: 16 },
    { direction: 'south', targetZoneId: 10, targetZoneName: '공동묘지 지하 1층', startX: 24, endX: 28, startY: 143, endY: 147, targetSpawnX: 32, targetSpawnY: 11 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_oakhaven',
    name: '마구간지기 잭슨 (Jackson)',
    x: 89,
    y: 72,
    routes: [
      { targetZoneId: 2, destinationName: '그란델 평원 (윈드밀 타운)', fare: 15 },
      { targetZoneId: 3, destinationName: '실바나 대삼림 (레이크우드)', fare: 25 },
      { targetZoneId: 4, destinationName: '보레아스 설원 (아이언프로스트 요새)', fare: 35 },
    ],
  };

  return { id: 1, name: '솔라리스 언덕 (솔라리아 구릉지)', englishName: 'Solaris Hills', biome: 'plains', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 2: 그란델 평원 (Grandell Plains) - 160x160
// ============================================================================
export function generateGrandellPlains(): ZoneData {
  const width = ZONE_SIZE, height = ZONE_SIZE;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('grass');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // West-to-East Main Highway (Y 78..82, X 0..120)
  for (let x = 0; x <= 120; x++) {
    for (let w = 78; w <= 82; w++) tiles[w][x] = 'cobblestone';
  }

  // Ancient Cromlech Sacred Circle (Center: X 60..95, Y 60..95)
  const cx = 78, cy = 78, rOuter = 20, rInner = 14;
  for (let y = 55; y <= 100; y++) {
    for (let x = 55; x <= 100; x++) {
      const dist = Math.hypot(x - cx, y - cy);
      if (dist <= 8) tiles[y][x] = 'altar';
      else if (dist <= rOuter && dist >= rInner) {
        if ((Math.atan2(y - cy, x - cx) * 12) % 2 > 0) tiles[y][x] = 'megalith';
        else tiles[y][x] = 'dirt_road';
      }
    }
  }

  // Windmill Town & Mill (East: X 110..145, Y 65..95)
  for (let y = 65; y <= 95; y++) for (let w = 118; w <= 121; w++) tiles[y][w] = 'dirt_road';
  for (let y = 74; y <= 86; y++) for (let x = 114; x <= 126; x++) tiles[y][x] = 'stone_pavement';
  for (let y = 68; y <= 73; y++) for (let x = 128; x <= 136; x++) {
    if (x === 128 || x === 136 || y === 68 || y === 73) tiles[y][x] = 'wooden_wall';
    else tiles[y][x] = 'house_roof';
  }
  for (let y = 88; y <= 93; y++) for (let x = 128; x <= 136; x++) {
    if (x === 128 || x === 136 || y === 88 || y === 93) tiles[y][x] = 'wooden_wall';
    else tiles[y][x] = 'house_roof';
  }

  // Windmill Town Grand Windmill
  tiles[70][132] = 'stone_pavement';

  // Vast Golden Wheat Fields (Southeast: X 90..155, Y 95..155)
  for (let y = 95; y <= 155; y++) {
    for (let x = 90; x <= 155; x++) {
      if ((x + y) % 3 !== 0) tiles[y][x] = 'farmland';
    }
  }

  for (let i = 0; i < 16; i++) {
    const ang = (i / 16) * Math.PI * 2;
    const mx = Math.round(78 + Math.cos(ang) * 18);
    const my = Math.round(78 + Math.sin(ang) * 18);
    props.push({ id: `z2_mega_${i}`, type: 'megalith', name: '크롬레크 거석', x: mx, y: my });
  }
  props.push({ id: 'z2_altar', type: 'altar_stone', name: '크롬레크 제단', x: 78, y: 78 });
  props.push({ id: 'z2_windmill', type: 'windmill', name: '윈드밀 대풍차', x: 132, y: 70 });
  props.push({ id: 'z2_cot1', type: 'house_cottage', name: '윈드밀 민가', x: 128, y: 74 });
  props.push({ id: 'z2_cot2', type: 'house_cottage', name: '윈드밀 제분소', x: 128, y: 88 });
  props.push({ id: 'z2_stable', type: 'horse_stable', name: '마구간지기 벤자민', x: 115, y: 85 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_cromlech', name: '고대 크롬레크 열석 유적', englishName: 'Cromlech Megaliths', x: 78, y: 78, description: '고대 영혼 신앙의 원형 열석과 룬 제단. 보름달 밤 신비한 빛을 발함.', icon: '🗿' },
    { id: 'landmark_windmill_town', name: '윈드밀 타운 & 대풍차', englishName: 'Windmill Town', x: 120, y: 80, description: '그란델 평원의 농업 중심지. 거대한 풍차와 밀가루 제분소, 마구간이 있음.', icon: '🌾' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'west', targetZoneId: 1, targetZoneName: '솔라리스 언덕', startX: 0, endX: 0, startY: 78, endY: 82, targetSpawnX: 154, targetSpawnY: 70 },
    { direction: 'south', targetZoneId: 3, targetZoneName: '실바나 대삼림', startX: 78, endX: 82, startY: 159, endY: 159, targetSpawnX: 80, targetSpawnY: 6 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_windmill_town',
    name: '마구간지기 벤자민 (Benjamin)',
    x: 115,
    y: 85,
    routes: [
      { targetZoneId: 1, destinationName: '솔라리아 구릉지 (오크헤이븐 / 솔라리움 성채)', fare: 15 },
    ],
  };

  return { id: 2, name: '그란델 평원', englishName: 'Grandell Plains', biome: 'plains', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 3: 실바나 대삼림 (Sylvana Forest) - 160x160
// ============================================================================
export function generateSylvanaForest(): ZoneData {
  const width = ZONE_SIZE, height = ZONE_SIZE;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('grass');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // Deep Forest Lake (Southwest: X 15..65, Y 80..130)
  for (let y = 80; y <= 130; y++) {
    for (let x = 15; x <= 65; x++) {
      const dist = Math.hypot((x - 40) * 1.2, y - 105);
      if (dist <= 26) tiles[y][x] = 'water';
    }
  }

  // North-to-South Highway (Y 0..159, X 78..82)
  for (let y = 0; y < height; y++) {
    for (let w = 78; w <= 82; w++) tiles[y][w] = 'cobblestone';
  }
  // East Highway to Ignis Wastes (X 78..159, Y 78..82)
  for (let x = 78; x < width; x++) {
    for (let w = 78; w <= 82; w++) tiles[w][x] = 'cobblestone';
  }

  // Sage's Barrow (Northwest: X 25..65, Y 25..65)
  for (let y = 30; y <= 55; y++) {
    for (let x = 30; x <= 55; x++) {
      if ((x + y) % 4 === 0) tiles[y][x] = 'stone_wall';
    }
  }
  tiles[42][42] = 'altar';

  // Lakewood Stilt Village (Southeast: X 85..125, Y 105..135)
  for (let y = 82; y <= 125; y++) for (let w = 96; w <= 99; w++) tiles[y][w] = 'dirt_road';
  for (let x = 85; x <= 125; x++) for (let w = 115; w <= 118; w++) tiles[w][x] = 'bridge';
  for (let y = 110; y <= 122; y++) for (let x = 100; x <= 114; x++) tiles[y][x] = 'stone_pavement';
  for (let y = 105; y <= 110; y++) for (let x = 116; x <= 124; x++) {
    if (x === 116 || x === 124 || y === 105 || y === 110) tiles[y][x] = 'wooden_wall';
    else tiles[y][x] = 'house_roof';
  }
  for (let y = 124; y <= 129; y++) for (let x = 116; x <= 124; x++) {
    if (x === 116 || x === 124 || y === 124 || y === 129) tiles[y][x] = 'wooden_wall';
    else tiles[y][x] = 'house_roof';
  }

  props.push({ id: 'z3_tomb', type: 'druid_tomb', name: '고대 현자의 고분', x: 42, y: 42 });
  props.push({ id: 'z3_cot1', type: 'house_cottage', name: '레이크우드 수상가옥', x: 118, y: 108 });
  props.push({ id: 'z3_cot2', type: 'house_cottage', name: '레이크우드 약초방', x: 118, y: 124 });
  props.push({ id: 'z3_stable', type: 'horse_stable', name: '마구간지기 가렛', x: 96, y: 112 });
  [
    { x: 30, y: 20 }, { x: 60, y: 25 }, { x: 65, y: 60 }, { x: 100, y: 30 },
    { x: 130, y: 40 }, { x: 135, y: 100 }, { x: 75, y: 135 }
  ].forEach((p, idx) => props.push({ id: `z3_tree_${idx}`, type: 'tree_darkwood', name: '실바나 거목', x: p.x, y: p.y }));

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_lakewood_village', name: '레이크우드 수상 가옥 마을', englishName: 'Lakewood Village', x: 105, y: 116, description: '호숫가 수변에 자리잡은 목조 데크 마을. 숲 약초상과 사냥꾼 캠프.', icon: '🌲' },
    { id: 'landmark_sages_barrow', name: '고대 현자의 고분군', englishName: "Sage's Barrow", x: 42, y: 42, description: '고대 숲의 현자들의 봉분. 덩굴 숲 속에 거대한 거미 둥지가 도사림.', icon: '🪦' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'north', targetZoneId: 2, targetZoneName: '그란델 평원', startX: 78, endX: 82, startY: 0, endY: 0, targetSpawnX: 80, targetSpawnY: 154 },
    { direction: 'south', targetZoneId: 5, targetZoneName: '네뷸라 늪지', startX: 78, endX: 82, startY: 159, endY: 159, targetSpawnX: 80, targetSpawnY: 6 },
    { direction: 'east', targetZoneId: 6, targetZoneName: '이그니스 황무지', startX: 159, endX: 159, startY: 78, endY: 82, targetSpawnX: 6, targetSpawnY: 80 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_lakewood',
    name: '마구간지기 가렛 (Garrett)',
    x: 96,
    y: 112,
    routes: [
      { targetZoneId: 1, destinationName: '솔라리아 구릉지 (오크헤이븐 / 솔라리움 성채)', fare: 25 },
      { targetZoneId: 5, destinationName: '네뷸라 늪지 (리드워터 나루)', fare: 25 },
      { targetZoneId: 6, destinationName: '이그니스 황무지 (미라주 오아시스 캠프)', fare: 30 },
    ],
  };

  return { id: 3, name: '실바나 대삼림', englishName: 'Sylvana Forest', biome: 'forest', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 4: 보레아스 설원 (Boreas Peaks) - 160x160
// ============================================================================
export function generateBoreasPeaks(): ZoneData {
  const width = ZONE_SIZE, height = ZONE_SIZE;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) {
      if ((x * 5 + y * 11) % 6 === 0) row.push('ice');
      else row.push('snow');
    }
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // South Cleared Cobblestone Highway (Y 90..159, X 78..82)
  for (let y = 90; y < height; y++) {
    for (let w = 78; w <= 82; w++) tiles[y][w] = 'cobblestone';
  }

  // Ironfrost Hold Fortress (South: X 60..95, Y 110..145)
  for (let y = 110; y <= 140; y++) for (let w = 70; w <= 73; w++) tiles[y][w] = 'dirt_road';
  for (let x = 55; x <= 85; x++) for (let w = 122; w <= 125; w++) tiles[w][x] = 'dirt_road';
  for (let y = 118; y <= 128; y++) for (let x = 65; x <= 77; x++) tiles[y][x] = 'stone_pavement';
  for (let y = 112; y <= 117; y++) for (let x = 55; x <= 63; x++) tiles[y][x] = 'house_roof';
  for (let y = 130; y <= 135; y++) for (let x = 80; x <= 88; x++) tiles[y][x] = 'house_roof';

  // Snow Giant's Throne & Glacier Peak (North: X 60..100, Y 25..55)
  for (let y = 25; y <= 55; y++) {
    for (let x = 60; x <= 100; x++) {
      const dist = Math.hypot(x - 80, y - 40);
      if (dist <= 18) tiles[y][x] = 'ice';
    }
  }
  tiles[40][80] = 'altar'; // Ice Throne

  props.push({ id: 'z4_fire', type: 'campfire', name: '아이언프로스트 화로', x: 70, y: 124 });
  props.push({ id: 'z4_cot1', type: 'house_cottage', name: '아이언프로스트 막사', x: 58, y: 114 });
  props.push({ id: 'z4_cot2', type: 'house_cottage', name: '아이언프로스트 모피상', x: 82, y: 132 });
  props.push({ id: 'z4_stable', type: 'horse_stable', name: '마구간지기 비요른', x: 76, y: 128 });
  [{ x: 35, y: 45 }, { x: 45, y: 80 }, { x: 120, y: 60 }, { x: 110, y: 120 }].forEach((p, idx) =>
    props.push({ id: `z4_pine_${idx}`, type: 'tree_snow_pine', name: '설산 침엽수', x: p.x, y: p.y })
  );

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_ironfrost_hold', name: '아이언프로스트 방한 요새', englishName: 'Ironfrost Hold', x: 70, y: 124, description: '혹한의 설산 요새 마을. 거대한 모닥불 화로와 모피 상인들이 상주함.', icon: '❄️' },
    { id: 'landmark_snow_giant', name: '스노우자이언트의 옥좌', englishName: "Snow Giant's Throne", x: 80, y: 40, description: '👑 BOSS: 스노우자이언트(Snow Giant)가 군림하는 만년설 침봉.', icon: '👑' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'south', targetZoneId: 1, targetZoneName: '솔라리스 언덕', startX: 78, endX: 82, startY: 159, endY: 159, targetSpawnX: 80, targetSpawnY: 6 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_ironfrost',
    name: '마구간지기 비요른 (Bjorn)',
    x: 76,
    y: 128,
    routes: [
      { targetZoneId: 1, destinationName: '솔라리스 언덕 (오크헤이븐 / 솔라리움 성채)', fare: 35 },
    ],
  };

  return { id: 4, name: '보레아스 설원', englishName: 'Boreas Peaks', biome: 'glacier', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 5: 네뷸라 늪지 (Nebula Marsh) - 160x160
// ============================================================================
export function generateNebulaMarsh(): ZoneData {
  const width = ZONE_SIZE, height = ZONE_SIZE;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) {
      if ((x * 7 + y * 13) % 4 === 0) row.push('swamp');
      else if ((x * 3 + y * 5) % 3 === 0) row.push('swamp_grass');
      else row.push('grass');
    }
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // North-to-South Highway (Y 0..100, X 78..82)
  for (let y = 0; y <= 100; y++) {
    for (let w = 78; w <= 82; w++) tiles[y][w] = 'cobblestone';
  }

  // West Boardwalk to Portal Gate (X 0..78, Y 78..82)
  for (let x = 0; x <= 78; x++) {
    for (let w = 78; w <= 82; w++) tiles[w][x] = 'bridge';
  }
  tiles[80][16] = 'altar'; // Portal Gate

  // Reedwater Stilt Village (Southeast: X 105..145, Y 105..145)
  for (let y = 82; y <= 135; y++) for (let w = 114; w <= 117; w++) tiles[y][w] = 'bridge';
  for (let x = 95; x <= 145; x++) for (let w = 120; w <= 123; w++) tiles[w][x] = 'bridge';
  for (let y = 115; y <= 128; y++) for (let x = 110; x <= 125; x++) tiles[y][x] = 'stone_pavement';
  for (let y = 108; y <= 113; y++) for (let x = 128; x <= 136; x++) {
    if (x === 128 || x === 136 || y === 108 || y === 113) tiles[y][x] = 'wooden_wall';
    else tiles[y][x] = 'house_roof';
  }
  for (let y = 130; y <= 135; y++) for (let x = 128; x <= 136; x++) {
    if (x === 128 || x === 136 || y === 130 || y === 135) tiles[y][x] = 'wooden_wall';
    else tiles[y][x] = 'house_roof';
  }

  props.push({ id: 'z5_portal', type: 'portal_gate', name: '루미나 성역 차원문', x: 16, y: 80 });
  props.push({ id: 'z5_cot1', type: 'house_cottage', name: '리드워터 수상가옥', x: 130, y: 110 });
  props.push({ id: 'z5_cot2', type: 'house_cottage', name: '리드워터 교역소', x: 130, y: 132 });
  props.push({ id: 'z5_stable', type: 'horse_stable', name: '마구간지기 핀', x: 116, y: 120 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_reedwater', name: '리드워터 수상 말뚝마을', englishName: 'Reedwater Stilt', x: 120, y: 120, description: '습지대 갈대숲에 떠 있는 수상 교역 마을. 루미나 탐사선과 낚시꾼들.', icon: '🌫️' },
    { id: 'landmark_lumina_portal', name: '루미나 차원문 나루터', englishName: 'Lumina Portal Harbor', x: 16, y: 80, description: '성스러운 호수 너머 루미나 성역섬으로 통하는 신비한 보라빛 차원문.', icon: '🔮' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'north', targetZoneId: 3, targetZoneName: '실바나 대삼림', startX: 78, endX: 82, startY: 0, endY: 0, targetSpawnX: 80, targetSpawnY: 154 },
    { direction: 'west', targetZoneId: 7, targetZoneName: '루미나 성역섬', startX: 0, endX: 0, startY: 78, endY: 82, targetSpawnX: 154, targetSpawnY: 80 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_reedwater',
    name: '마구간지기 핀 (Finn)',
    x: 116,
    y: 120,
    routes: [
      { targetZoneId: 3, destinationName: '실바나 대삼림 (레이크우드)', fare: 25 },
      { targetZoneId: 7, destinationName: '루미나 성역섬 (루미나 크리스탈 시티)', fare: 40 },
    ],
  };

  return { id: 5, name: '네뷸라 늪지', englishName: 'Nebula Marsh', biome: 'swamp', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 6: 이그니스 황무지 (Ignis Wastes) - 160x160
// ============================================================================
export function generateIgnisWastes(): ZoneData {
  const width = ZONE_SIZE, height = ZONE_SIZE;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) {
      if ((x * 3 + y * 7) % 5 === 0) row.push('lava');
      else row.push('sand');
    }
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // West Road from Sylvana Forest (X 0..70, Y 78..82)
  for (let x = 0; x <= 70; x++) {
    for (let w = 78; w <= 82; w++) tiles[w][x] = 'dirt_road';
  }

  // Mirage Oasis Camp (West: X 35..75, Y 65..95)
  for (let y = 68; y <= 92; y++) {
    for (let x = 38; x <= 72; x++) {
      const dist = Math.hypot(x - 55, y - 80);
      if (dist <= 8) tiles[y][x] = 'water'; // Oasis pond
      else if (dist <= 18) tiles[y][x] = 'stone_pavement';
    }
  }
  for (let y = 72; y <= 77; y++) for (let x = 60; x <= 68; x++) tiles[y][x] = 'house_roof';

  // Ignar the Dragon's Caldera (Northeast: X 100..145, Y 25..65)
  for (let y = 25; y <= 65; y++) {
    for (let x = 100; x <= 145; x++) {
      const dist = Math.hypot(x - 122, y - 45);
      if (dist <= 16) tiles[y][x] = 'lava';
    }
  }
  tiles[45][122] = 'altar';

  props.push({ id: 'z6_tent1', type: 'desert_tent', name: '오아시스 천막', x: 48, y: 74 });
  props.push({ id: 'z6_tent2', type: 'desert_tent', name: '원정대 막사', x: 62, y: 74 });
  props.push({ id: 'z6_stable', type: 'horse_stable', name: '마구간지기 칼리드', x: 56, y: 84 });
  props.push({ id: 'z6_palm1', type: 'palm_tree', name: '오아시스 야자수', x: 46, y: 86 });
  props.push({ id: 'z6_palm2', type: 'palm_tree', name: '오아시스 야자수', x: 64, y: 86 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_mirage_camp', name: '미라주 오아시스 캠프', englishName: 'Mirage Oasis Camp', x: 55, y: 80, description: '황무지 탐사를 위한 군수 야영지. 물 보급소와 원정대 막사, 마구간 위치.', icon: '🏕️' },
    { id: 'landmark_dragon_ignar', name: '화룡 이그나르의 분화구', englishName: "Ignar the Dragon's Caldera", x: 122, y: 45, description: '👑 BOSS: 불의 용 이그나르(Ignar)가 서식하는 끓어오르는 용암 분화구.', icon: '🐲' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'west', targetZoneId: 3, targetZoneName: '실바나 대삼림', startX: 0, endX: 0, startY: 78, endY: 82, targetSpawnX: 154, targetSpawnY: 80 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_mirage_camp',
    name: '마구간지기 칼리드 (Khalid)',
    x: 56,
    y: 84,
    routes: [
      { targetZoneId: 3, destinationName: '실바나 대삼림 (레이크우드)', fare: 30 },
    ],
  };

  return { id: 6, name: '이그니스 황무지', englishName: 'Ignis Wastes', biome: 'desert', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 7: 루미나 성역섬 (Isle of Lumina) - 160x160
// ============================================================================
export function generateIsleOfLumina(): ZoneData {
  const width = ZONE_SIZE, height = ZONE_SIZE;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) {
      const dist = Math.hypot(x - 80, y - 80);
      if (dist > 72) row.push('water'); // Surrounding sacred lake
      else if (dist <= 30) row.push('crystal_floor');
      else row.push('grass');
    }
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // Crystal Bridge from East Harbor (X 130..159, Y 78..82)
  for (let x = 130; x < width; x++) {
    for (let w = 78; w <= 82; w++) tiles[w][x] = 'bridge';
  }
  for (let x = 70; x <= 130; x++) {
    for (let w = 78; w <= 82; w++) tiles[w][x] = 'cobblestone';
  }

  // Lumina Crystal City Plaza (East: X 95..135, Y 65..95)
  for (let y = 68; y <= 92; y++) for (let x = 100; x <= 130; x++) tiles[y][x] = 'stone_pavement';
  for (let y = 72; y <= 77; y++) for (let x = 105; x <= 115; x++) tiles[y][x] = 'house_roof';
  for (let y = 83; y <= 88; y++) for (let x = 105; x <= 115; x++) tiles[y][x] = 'house_roof';
  tiles[80][120] = 'altar'; // Crystal Spire

  // Sir Baran's Citadel (Northwest: X 35..75, Y 35..75)
  for (let y = 35; y <= 75; y++) {
    for (let x = 35; x <= 75; x++) {
      if (x === 35 || x === 75 || y === 35 || y === 75) tiles[y][x] = 'stone_wall';
      else tiles[y][x] = 'crystal_floor';
    }
  }
  tiles[55][55] = 'altar'; // Sir Baran's Throne

  props.push({ id: 'z7_spire', type: 'crystal_spire', name: '루미나 백색 수정탑', x: 120, y: 80 });
  props.push({ id: 'z7_stable', type: 'horse_stable', name: '마구간지기 에일린', x: 118, y: 84 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_lumina_city', name: '루미나 크리스탈 시티', englishName: 'Lumina Crystal City', x: 118, y: 80, description: '백색 수정탑과 신성 대성당의 도시. 영혼의 가호가 깃든 성역.', icon: '🏰' },
    { id: 'landmark_sir_baran', name: '바란 경의 고성', englishName: "Sir Baran's Citadel", x: 55, y: 55, description: '👑 BOSS: 거대 기사령 바란 경(Sir Baran)의 외딴 수정 요새.', icon: '🔮' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'east', targetZoneId: 5, targetZoneName: '네뷸라 늪지', startX: 159, endX: 159, startY: 78, endY: 82, targetSpawnX: 6, targetSpawnY: 80 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_lumina_city',
    name: '마구간지기 에일린 (Aileen)',
    x: 118,
    y: 84,
    routes: [
      { targetZoneId: 5, destinationName: '네뷸라 늪지 (리드워터 나루)', fare: 40 },
    ],
  };

  return { id: 7, name: '루미나 성역섬', englishName: 'Isle of Lumina', biome: 'crystal', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 8: 솔라리스 성 (Solaris Castle) - 128x128
// ============================================================================
export function generateSolarisCastle(): ZoneData {
  const width = 128, height = 128;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('stone_pavement');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // 외곽 성벽 (Stone Wall)
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (x <= 4 || x >= width - 5 || y <= 4 || y >= height - 5) {
        if (!(y >= height - 6 && x >= 58 && x <= 70)) tiles[y][x] = 'stone_wall';
      }
    }
  }

  // 남문 출구 게이트 (64, 122)
  tiles[122][64] = 'castle_gate';
  props.push({ id: 'z8_gate_exit', type: 'castle_gate', name: '솔라리스 성 남문 출구', x: 64, y: 122 });

  // 대궁전 (황금 옥좌실: x 34..94, y 16..60)
  for (let y = 16; y <= 60; y++) for (let x = 34; x <= 94; x++) {
    if (x === 34 || x === 94 || y === 16 || y === 60) tiles[y][x] = 'stone_wall';
    else tiles[y][x] = 'stone_pavement';
  }
  tiles[24][64] = 'altar'; // 황금 대옥좌
  props.push({ id: 'z8_throne', type: 'palace', name: '솔라리스 황금 대옥좌', x: 64, y: 24 });

  // 중앙 분수 정원 (64, 80)
  for (let y = 74; y <= 86; y++) for (let x = 58; x <= 70; x++) {
    if (Math.hypot(x - 64, y - 80) <= 5) tiles[y][x] = 'water';
  }
  props.push({ id: 'z8_fountain', type: 'altar_stone', name: '왕궁 중앙 분수대', x: 64, y: 80 });

  // 서측 병영 & 동측 도서관
  props.push({ id: 'z8_barracks', type: 'house_cottage', name: '왕실 근위 기사단 병영', x: 31, y: 88 });
  props.push({ id: 'z8_library', type: 'house_cottage', name: '솔라리스 왕립 대도서관', x: 97, y: 88 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_z8_throne', name: '솔라리스 황금 대옥좌', englishName: 'Royal Throne Room', x: 64, y: 24, description: '솔라리스 왕국의 국왕과 수호 기사단.', icon: '👑' },
    { id: 'landmark_z8_gate', name: '솔라리스 성 남문 출구', englishName: 'Castle South Gate', x: 64, y: 122, description: '솔라리스 구릉지 방면 귀환 관문.', icon: '🏰' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'south', targetZoneId: 1, targetZoneName: '솔라리스 언덕 (솔라리아 구릉지)', startX: 60, endX: 68, startY: 122, endY: 124, targetSpawnX: 18, targetSpawnY: 26 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_solaris_castle',
    name: '왕실 마구간장 고드윈',
    x: 52, y: 105,
    routes: [{ targetZoneId: 1, destinationName: '솔라리아 구릉지 (오크헤이븐)', fare: 10 }]
  };

  return { id: 8, name: '솔라리스 성', englishName: 'Solaris Castle', biome: 'stone', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 9: 솔라리스 하우징 지구 (Housing Zone) - 160x160
// ============================================================================
export function generateHousingZone(): ZoneData {
  const width = 160, height = 160;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('grass');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // 십자형 대가도 (너비 6)
  for (let y = 0; y < height; y++) for (let x = 77; x <= 83; x++) tiles[y][x] = 'cobblestone';
  for (let x = 0; x < width; x++) for (let y = 77; y <= 83; y++) tiles[y][x] = 'cobblestone';

  // 북문 정문 출구 (80, 10)
  tiles[10][80] = 'castle_gate';
  props.push({ id: 'z9_gate_exit', type: 'castle_gate', name: '하우징 지구 정문 출구', x: 80, y: 10 });

  // 중앙 분수 광장 (80, 80)
  for (let y = 68; y <= 92; y++) for (let x = 68; x <= 92; x++) {
    tiles[y][x] = 'stone_pavement';
    if (Math.hypot(x - 80, y - 80) <= 6) tiles[y][x] = 'water';
  }
  props.push({ id: 'z9_fountain', type: 'altar_stone', name: '하우징 중앙 분수대', x: 80, y: 80 });

  // 주택 단지 구획 16개 (코티지 배치)
  const offsets = [-45, -20, 20, 45];
  let pIdx = 1;
  offsets.forEach(oy => {
    offsets.forEach(ox => {
      const bx = 80 + ox, by = 80 + oy;
      if (Math.hypot(ox, oy) > 15) {
        props.push({ id: `z9_plot_${pIdx}`, type: 'house_cottage', name: `개인 영지 주택 ${pIdx}호`, x: bx, y: by });
        pIdx++;
      }
    });
  });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_z9_plaza', name: '하우징 지구 중앙 분수 광장', englishName: 'Housing Central Plaza', x: 80, y: 80, description: '주민들의 만남과 거래 광장.', icon: '⛲' },
    { id: 'landmark_z9_gate', name: '하우징 지구 정문 출구', englishName: 'Housing Gate', x: 80, y: 10, description: '솔라리스 구릉지 방면 귀환 관문.', icon: '🏡' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'north', targetZoneId: 1, targetZoneName: '솔라리스 언덕 (솔라리아 구릉지)', startX: 77, endX: 83, startY: 8, endY: 12, targetSpawnX: 8, targetSpawnY: 56 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_housing',
    name: '하우징 역마차지기 토마스',
    x: 88, y: 72,
    routes: [{ targetZoneId: 1, destinationName: '솔라리아 구릉지 (오크헤이븐)', fare: 10 }]
  };

  return { id: 9, name: '솔라리스 하우징 지구', englishName: 'Solaris Housing Zone', biome: 'plains', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 10: 공동묘지 지하 1층 (Cemetery B1) - 64x64
// ============================================================================
export function generateCemeteryB1(): ZoneData {
  const width = 64, height = 64;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('stone_pavement');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // 외곽 암반 벽
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (x <= 3 || x >= width - 4 || y <= 3 || y >= height - 4) tiles[y][x] = 'stone_wall';
    }
  }

  // 미로형 석벽
  for (let i = 12; i <= 52; i += 10) {
    for (let j = 8; j <= 56; j++) {
      if (j % 8 !== 0) {
        tiles[i][j] = 'stone_wall';
        tiles[j][i] = 'stone_wall';
      }
    }
  }

  // 지상 탈출구 (32, 6) 및 중앙 진입 통로 개방
  for (let y = 5; y <= 18; y++) tiles[y][32] = 'stone_pavement';
  tiles[6][32] = 'castle_gate';
  props.push({ id: 'z10_stairs_up', type: 'castle_gate', name: '지상 무덤 탈출 계단', x: 32, y: 6 });

  // 지하 2층 하강 계단 (55, 55)
  tiles[55][55] = 'castle_gate';
  props.push({ id: 'z10_stairs_down', type: 'dungeon_gate', name: '지하 2층 진입 계단', x: 55, y: 55 });

  props.push({ id: 'z10_bones_1', type: 'altar_stone', name: '해골 납골당', x: 18, y: 20 });
  props.push({ id: 'z10_bones_2', type: 'altar_stone', name: '부패한 좀비 굴', x: 45, y: 20 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_z10_up', name: '지상 탈출 계단', englishName: 'Stairs to Surface', x: 32, y: 6, description: '솔라리스 구릉지 지상 무덤 방면 탈출구.', icon: '🪜' },
    { id: 'landmark_z10_down', name: '지하 2층 진입 계단', englishName: 'Stairs to B2', x: 55, y: 55, description: '더 깊은 지하 납골당으로 연결.', icon: '💀' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'north', targetZoneId: 1, targetZoneName: '솔라리스 언덕 (솔라리아 구릉지)', startX: 30, endX: 34, startY: 4, endY: 8, targetSpawnX: 26, targetSpawnY: 150 },
    { direction: 'south', targetZoneId: 11, targetZoneName: '공동묘지 지하 2층', startX: 53, endX: 57, startY: 53, endY: 57, targetSpawnX: 8, targetSpawnY: 11 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_z10_dummy',
    name: '던전 가이드', x: 32, y: 8, routes: []
  };

  return { id: 10, name: '공동묘지 지하 1층', englishName: 'Cemetery B1', biome: 'dungeon', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 11: 공동묘지 지하 2층 (Cemetery B2) - 64x64
// ============================================================================
export function generateCemeteryB2(): ZoneData {
  const width = 64, height = 64;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('stone_pavement');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // 외곽 암반 벽 & 침수 구역
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (x <= 3 || x >= width - 4 || y <= 3 || y >= height - 4) tiles[y][x] = 'stone_wall';
      if ((x >= 15 && x <= 25 && y >= 35 && y <= 48) || (x >= 40 && x <= 52 && y >= 15 && y <= 28)) {
        tiles[y][x] = 'water'; // 침수된 지하수
      }
    }
  }

  // 지하 2층 미로 벽
  for (let y = 10; y <= 54; y += 8) {
    for (let x = 8; x <= 56; x++) {
      if ((x + y) % 6 !== 0) tiles[y][x] = 'stone_wall';
    }
  }

  // 상층부(지하 1층) 귀환 계단 (8, 8)
  tiles[8][8] = 'castle_gate';
  props.push({ id: 'z11_stairs_up', type: 'castle_gate', name: '지하 1층 귀환 계단', x: 8, y: 8 });

  // 지하 3층 대묘당 대문 (32, 55)
  tiles[55][32] = 'castle_gate';
  props.push({ id: 'z11_stairs_down', type: 'dungeon_gate', name: '지하 3층 대묘당 관문', x: 32, y: 55 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_z11_up', name: '지하 1층 귀환 계단', englishName: 'Stairs to B1', x: 8, y: 8, description: '지하 1층 상층부로 귀환.', icon: '🪜' },
    { id: 'landmark_z11_down', name: '지하 3층 대묘당 관문', englishName: 'Gate to B3 Boss Chamber', x: 32, y: 55, description: '3대 보스가 잠든 최하층 대묘당.', icon: '💀' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'north', targetZoneId: 10, targetZoneName: '공동묘지 지하 1층', startX: 6, endX: 10, startY: 6, endY: 10, targetSpawnX: 55, targetSpawnY: 51 },
    { direction: 'south', targetZoneId: 12, targetZoneName: '공동묘지 지하 3층 (대묘당)', startX: 30, endX: 34, startY: 53, endY: 57, targetSpawnX: 64, targetSpawnY: 20 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_z11_dummy',
    name: '던전 가이드', x: 8, y: 10, routes: []
  };

  return { id: 11, name: '공동묘지 지하 2층', englishName: 'Cemetery B2', biome: 'dungeon', width, height, tiles, props, landmarks, exits, stableMaster };
}

// ============================================================================
// ZONE 12: 공동묘지 지하 3층 (대묘당, 3대 보스) - 128x128
// ============================================================================
export function generateCemeteryB3(): ZoneData {
  const width = 128, height = 128;
  const tiles: ZoneTileType[][] = [];

  for (let y = 0; y < height; y++) {
    const row: ZoneTileType[] = [];
    for (let x = 0; x < width; x++) row.push('stone_pavement');
    tiles.push(row);
  }
  const props: ZoneProp[] = [];

  // 외곽 암반 벽
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (x <= 5 || x >= width - 6 || y <= 5 || y >= height - 6) tiles[y][x] = 'stone_wall';
    }
  }

  // 십자형 대통로
  for (let y = 15; y <= height - 15; y++) {
    for (let dx = -4; dx <= 4; dx++) tiles[y][64 + dx] = 'stone_pavement';
  }
  for (let x = 15; x <= width - 15; x++) {
    for (let dy = -4; dy <= 4; dy++) tiles[64 + dy][x] = 'stone_pavement';
  }

  // B2 귀환 계단 (64, 15)
  tiles[15][64] = 'castle_gate';
  props.push({ id: 'z12_stairs_up', type: 'castle_gate', name: '지하 2층 귀환 계단', x: 64, y: 15 });

  // [BOSS 1] 북서 구석 (26, 26): 해골화된 루나리스 병사 (최하급 보병)
  props.push({ id: 'z12_boss_skel_altar', type: 'altar_stone', name: '💀 [BOSS 1] 해골화된 루나리스 병사의 묘실 (크기 3배, HP 1350)', x: 26, y: 26 });

  // [BOSS 2] 북동 구석 (102, 26): 좀비화된 루나리스 병사 (최하급 보병)
  props.push({ id: 'z12_boss_abom_altar', type: 'altar_stone', name: '🧟 [BOSS 2] 좀비화된 루나리스 병사의 구덩이 (크기 3배, HP 1500)', x: 102, y: 26 });

  // [BOSS 3] 남서 구석 (26, 102): 흡혈박쥐 (정찰 사역마)
  props.push({ id: 'z12_boss_bat_altar', type: 'altar_stone', name: '🦇 [BOSS 3] 흡혈박쥐의 동굴 (크기 3배, HP 1200)', x: 26, y: 102 });

  // [보물고] 남동 구석 (102, 102): 고대 왕가의 황금 보물고
  props.push({ id: 'z12_treasure_vault', type: 'palace', name: '💎 고대 왕가의 황금 보물고', x: 102, y: 102 });

  const landmarks: ZoneLandmark[] = [
    { id: 'landmark_z12_up', name: '지하 2층 귀환 계단', englishName: 'Stairs to B2', x: 64, y: 15, description: '상층부로 퇴각하는 관문.', icon: '🪜' },
    { id: 'landmark_z12_boss1', name: '💀 해골화된 루나리스 병사 (보스 1)', englishName: 'Skeletonized Lunaris Soldier', x: 26, y: 26, description: '루나리스 왕국 최하급 척후병 (3배 크기, HP: 1350).', icon: '💀' },
    { id: 'landmark_z12_boss2', name: '🧟 좀비화된 루나리스 병사 (보스 2)', englishName: 'Zombified Lunaris Soldier', x: 102, y: 26, description: '루나리스 왕국 최하급 보병 (3배 크기, HP: 1500).', icon: '🧟' },
    { id: 'landmark_z12_boss3', name: '🦇 흡혈박쥐 (보스 3)', englishName: 'Vampire Bat', x: 26, y: 102, description: '루나리스 왕국 정찰 사역마 (3배 크기, HP: 1200).', icon: '🦇' },
  ];

  const exits: ZoneExit[] = [
    { direction: 'north', targetZoneId: 11, targetZoneName: '공동묘지 지하 2층', startX: 62, endX: 66, startY: 13, endY: 17, targetSpawnX: 32, targetSpawnY: 51 },
  ];

  const stableMaster: StableMasterConfig = {
    id: 'npc_stable_z12_dummy',
    name: '던전 가이드', x: 64, y: 20, routes: []
  };

  return { id: 12, name: '공동묘지 지하 3층 (대묘당)', englishName: 'Cemetery B3', biome: 'dungeon', width, height, tiles, props, landmarks, exits, stableMaster };
}

export function getZoneData(zoneId: number): ZoneData {
  switch (zoneId) {
    case 1: return generateSolariaHills();
    case 2: return generateGrandellPlains();
    case 3: return generateSylvanaForest();
    case 4: return generateBoreasPeaks();
    case 5: return generateNebulaMarsh();
    case 6: return generateIgnisWastes();
    case 7: return generateIsleOfLumina();
    case 8: return generateSolarisCastle();
    case 9: return generateHousingZone();
    case 10: return generateCemeteryB1();
    case 11: return generateCemeteryB2();
    case 12: return generateCemeteryB3();
    default: return generateSolariaHills();
  }
}
